/**
 * Based on `withRouter()` implementation:
 * https://github.com/ReactTraining/react-router/blob/master/packages/react-router/modules/withRouter.js
 */
import React, { ComponentType } from 'react';

import hoistStatics from 'hoist-non-react-statics';
import {
  Route,
  RouterHistory,
} from 'react-router-dom';

type HistoryPushArgs = {
  path: string;
  hash?: string;
  search?: string;
  state?: any;
  retainQueryString?: boolean;
};
export type History = RouterHistory & {
  push: (args: HistoryPushArgs) => void;
};
export type WithHistoryInjectedProps = {
  history: History;
  location: {
    hash: string;
    pathname: string;
    search: string;
    state?: unknown;
  };
};
export function withHistory<P = any>(
  Component: ComponentType<P>,
) {
  const WrappedComponent = (props) => {
    const {
      wrappedComponentRef,
      ...remainingProps
    } = props;
    return (
      <Route
        render={({
          history: { push, ...rest },
          location,
        }) => {
          const { search: existingQueryString } = location;
          const enhancedHistory = {
            ...rest,
            push: ({
              hash,
              path: pathname,
              search,
              state,
              retainQueryString = true,
            }) => {
              const forwardedQueryString = search
                || (retainQueryString && existingQueryString);
              push(
                {
                  pathname,
                  search: forwardedQueryString,
                  hash,
                },
                state,
              );
            },
          };
          return (
            <Component
              {...remainingProps}
              history={enhancedHistory}
              ref={wrappedComponentRef}
            />
          );
        }}
      />
    );
  };

  return hoistStatics<History, P>(
    WrappedComponent,
    Component,
  );
}
